import React from 'react';
import { Navigate } from 'react-router-dom';
import { RowDrawer } from './components';
import { ProductsStepView } from './ProductsStepView';
import { useProductsStep } from './useProductsStep';

export type ProductsStepProps = {
    /** Where Back goes, and where the step goes when it opens without a preview: Names. */
    backPath: string;
    /** Where a completed import goes, and where the step goes when it opens on one: Result. */
    nextPath: string;
    /**
     * Result offers Undo this import (the wizard): the suggested-fixes
     * dialog then says the whole import can be undone there.
     */
    showUndoNote?: boolean;
};

/**
 * Products: every row of the file as the last preview answered it. The
 * wizard mounts it at `/onboarding/products`; the in-shell import page
 * mounts it with the branch fixed. Both pass Names as `backPath`, the action
 * bar's Back. Renders inside a `CatalogImportProvider`.
 *
 * The rows come from the store's preview, so a step that opens without one
 * (the store lives in memory, and a reload empties it) goes back to Names,
 * which goes on back to Upload and Branch as they need. A step that opens
 * on a file already imported (browser Back from Result) goes on to Result
 * instead: its Import would book the same stock again. Both are decided
 * once, as the step opens, as Names decides it, so the step's own move to
 * Result after an import is not taken twice.
 *
 * The row drawer is mounted here, beside the View: it reads the store and
 * the address itself, and takes the step's exclusion handlers, its re-check
 * request and its bulk toast, so its Exclude / Include and Save act as the
 * table's, and its group pick toasts and undoes as a bulk fix does.
 */
export const ProductsStep: React.FC<ProductsStepProps> = ({
    backPath,
    nextPath,
    showUndoNote = false,
}) => {
    const {
        hasPreview,
        hasRecord,
        onRequestRecheck,
        onGroupFixApplied,
        ...step
    } = useProductsStep({ backPath, nextPath });
    const [openedWithoutPreview] = React.useState(!hasPreview);
    const [openedImported] = React.useState(hasRecord);

    if (openedWithoutPreview) {
        return <Navigate replace to={backPath} />;
    }
    if (openedImported) {
        return <Navigate replace to={nextPath} />;
    }

    return (
        <>
            <ProductsStepView {...step} showUndoNote={showUndoNote} />
            <RowDrawer
                onExcludeRow={step.onExcludeRow}
                onGroupFixApplied={onGroupFixApplied}
                onIncludeRow={step.onIncludeRow}
                onRecheck={onRequestRecheck}
            />
        </>
    );
};
